import { motion } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import Logo from "../components/Logo";
import {
  CheckCircle, Video, Search, ShieldCheck, Calendar, MessageCircle,
  FileSignature, Star, ArrowRight, Bed, Building2, Home, Zap, Clock, CreditCard,
  Sparkles, Key, ScanFace, Bot, CalendarCheck, Check, Menu, X
} from "lucide-react";
import { useState } from "react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.5 } }),
};

export default function LandingPage() {
  const { user } = useAuth();
  const [mobileMenu, setMobileMenu] = useState(false);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleLogin = () => {
    window.location.href = "/auth";
  };

  const dashboardLink = user?.role === "landlord" ? "/landlord-dashboard" : user?.role === "admin" ? "/admin" : "/dashboard";

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      {/* ─── NAV ─── */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-[#faf8f3]/80 border-b border-black/[0.06]" data-testid="landing-nav">
        <div className="max-w-7xl mx-auto px-5 md:px-12 lg:px-24 flex items-center justify-between h-16">
          <Logo />

          <div className="hidden md:flex items-center gap-8">
            <Link to="/browse" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c] transition-colors" data-testid="nav-browse-link">
              Browse
            </Link>
            <Link to="/contact" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c] transition-colors" data-testid="nav-contact-link">
              Contact
            </Link>
            {user && (
              <Link to="/messages" className="text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c] transition-colors">
                Messages
              </Link>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!user ? (
              <>
                <Link to="/browse" className="md:hidden text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c]" data-testid="nav-browse-mobile">
                  Browse
                </Link>
                <Button data-testid="nav-signin-btn" onClick={handleLogin} className="rounded-full bg-[#1a6b3c] hover:bg-[#15562f] text-white px-5 text-sm">
                  Sign In
                </Button>
              </>
            ) : (
              <>
                <Link to="/browse" className="md:hidden text-sm font-medium text-[#0d0d0d] hover:text-[#1a6b3c]">
                  Browse
                </Link>
                <Link to={dashboardLink}>
                  <Button data-testid="nav-dashboard-btn" className="rounded-full bg-[#1a6b3c] hover:bg-[#15562f] text-white px-5 text-sm">
                    Dashboard
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ─── HERO ─── */}
      <section className="relative px-5 md:px-12 lg:px-24 pt-16 pb-20 md:pt-24 md:pb-32">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" animate="visible" className="max-w-3xl">
            <motion.p variants={fadeUp} custom={0} className="uppercase tracking-[0.2em] text-xs font-semibold text-[#1a6b3c] mb-6">
              Launching in VA, MD & DC 2026
            </motion.p>
            <motion.h1
              variants={fadeUp} custom={1}
              className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-[#0d0d0d] mb-6 font-['Fraunces'] leading-[1.1]"
              data-testid="hero-headline"
            >
              Find. Screen.<br />
              <span className="text-[#1a6b3c]">Move in.</span>
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="text-base md:text-lg text-[#888] mb-10 max-w-xl leading-relaxed">
              Rooms, apartments & houses — verified, background-checked, flexible leases. One platform. Zero chaos.
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="flex flex-wrap gap-3">
              {!user ? (
                <>
                  <Button data-testid="cta-need-place" onClick={handleLogin} className="rounded-full bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] px-6 sm:px-8 h-12 text-sm sm:text-base font-semibold">
                    <Bed className="w-5 h-5 mr-2" /> I Need a Place
                  </Button>
                  <Button data-testid="cta-list-place" onClick={handleLogin} variant="outline" className="rounded-full border-[#0d0d0d] text-[#0d0d0d] hover:bg-[#0d0d0d] hover:text-white px-6 sm:px-8 h-12 text-sm sm:text-base font-semibold">
                    <Building2 className="w-5 h-5 mr-2" /> I Have a Place
                  </Button>
                  <Button data-testid="cta-military" onClick={handleLogin} variant="outline" className="rounded-full border-[#1a6b3c] text-[#1a6b3c] hover:bg-[#1a6b3c] hover:text-white px-6 sm:px-8 h-12 text-sm sm:text-base font-semibold">
                    <ShieldCheck className="w-5 h-5 mr-2" /> Military Family
                  </Button>
                </>
              ) : (
                <Button data-testid="cta-browse" onClick={() => window.location.href = "/browse"} className="rounded-full bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] px-8 h-12 text-base font-semibold">
                  Browse Listings <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              )}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ─── PROPERTY TYPES ─── */}
      <section className="px-5 md:px-12 lg:px-24 pb-20 md:pb-24">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { type: "room", label: "A Room", price: "$400 – $900/mo", desc: "Private room in a shared home", img: "https://images.unsplash.com/photo-1662454419716-c4c504728811?w=600" },
            { type: "apartment", label: "An Apartment", price: "$800 – $1,500/mo", desc: "Full unit, just for you", img: "https://images.unsplash.com/photo-1666282167632-c613fbeb163c?w=600" },
            { type: "house", label: "A House", price: "$1,200 – $2,500/mo", desc: "The whole place, yours", img: "https://images.pexels.com/photos/279719/pexels-photo-279719.jpeg?w=600" },
          ].map((item, i) => (
            <motion.a
              key={item.type}
              href={`/browse?property_type=${item.type}`}
              initial="hidden" whileInView="visible" viewport={{ once: true }}
              variants={fadeUp} custom={i}
              data-testid={`property-type-${item.type}`}
              className="group relative rounded-3xl overflow-hidden aspect-[3/4] block"
            >
              <img src={item.img} alt={item.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                <p className="text-sm font-medium opacity-80">{item.price}</p>
                <h3 className="text-2xl font-black mb-1 font-['Fraunces']">{item.label}</h3>
                <p className="text-sm opacity-70">{item.desc}</p>
                <div className="mt-3 flex items-center gap-2 text-[#c8f563] font-medium text-sm group-hover:gap-3 transition-all">
                  Browse <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </motion.a>
          ))}
        </div>
      </section>

      {/* ─── MARQUEE ─── */}
      <section className="overflow-hidden py-5 border-y-2 border-[#0d0d0d] bg-[#c8f563]">
        <div className="flex animate-marquee whitespace-nowrap">
          {["Rooms", "Apartments", "Houses", "Video Intros", "AI Screening", "Background Checks", "Military Friendly", "Short & Long-Term Leases", "No More Craigslist", "Verified Listings",
            "Rooms", "Apartments", "Houses", "Video Intros", "AI Screening", "Background Checks", "Military Friendly", "Short & Long-Term Leases", "No More Craigslist", "Verified Listings"
          ].map((text, i) => (
            <span key={i} className="flex items-center gap-3 mx-6 text-lg font-black text-[#0d0d0d] font-['Fraunces']">
              {text} <span className="w-1.5 h-1.5 rounded-full bg-[#0d0d0d] opacity-30" />
            </span>
          ))}
        </div>
      </section>

      {/* ─── FEATURES BENTO ─── */}
      <section className="px-5 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-14">
            <motion.h2 variants={fadeUp} custom={0} className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-[#0d0d0d] font-['Fraunces']">
              Everything in <em className="text-[#1a6b3c]">one place.</em>
            </motion.h2>
            <motion.p variants={fadeUp} custom={1} className="text-[#888] mt-3 max-w-xl text-sm">
              No third-party portals. No app switching. No confusion. Just one seamless experience from search to keys.
            </motion.p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { Icon: Video, title: "Video Intros", desc: "Real faces before any showing. 60-second video intros from every applicant.", color: "#1a6b3c" },
              { Icon: ShieldCheck, title: "Background Check", desc: "Criminal, eviction, credit & identity — one report, results in under 24 hours.", color: "#0d0d0d" },
              { Icon: Sparkles, title: "AI Screening Score", desc: "Every applicant scored for budget fit, reliability, and compatibility.", color: "#e8633a" },
              { Icon: CalendarCheck, title: "Showing Scheduler", desc: "Set times. Book direct. Auto-reminders sent. Zero phone tag.", color: "#1a6b3c" },
              { Icon: MessageCircle, title: "In-App Messaging", desc: "Chat directly. No external contact sharing. Everything stays on platform.", color: "#0d0d0d" },
              { Icon: FileSignature, title: "Digital Lease Signing", desc: "Sign your lease digitally. Choose your lease length. Get your keys.", color: "#e8633a" },
              { Icon: Star, title: "Two-Way Ratings", desc: "Renters rate landlords. Landlords rate renters. Everyone stays accountable.", color: "#1a6b3c" },
              { Icon: CreditCard, title: "Secure Payments", desc: "Pay rent, deposits, and background checks securely through the platform.", color: "#0d0d0d" },
              { Icon: CheckCircle, title: "Verified Listings", desc: "Every listing verified. Every landlord screened. Trust built in.", color: "#1a6b3c" },
            ].map((feature, i) => (
              <motion.div
                key={feature.title}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={i}
                className="bg-white rounded-2xl p-6 border border-black/[0.06] shadow-[0_4px_24px_rgba(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-[0_8px_32px_rgba(0,0,0,0.08)] transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ backgroundColor: feature.color + "10" }}>
                  <feature.Icon className="w-5 h-5" style={{ color: feature.color }} />
                </div>
                <h3 className="font-black text-[#0d0d0d] mb-2 font-['Fraunces']">{feature.title}</h3>
                <p className="text-sm text-[#888] leading-relaxed">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── HOW IT WORKS ─── */}
      <section className="px-5 md:px-12 lg:px-24 py-20 md:py-32 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-14">
            <motion.p variants={fadeUp} custom={0} className="uppercase tracking-[0.2em] text-xs font-semibold text-[#c8f563] mb-4">
              Stupidly Simple
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-white font-['Fraunces']">
              4 steps. That's <em className="text-[#c8f563]">it.</em>
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { num: "01", title: "Create your profile & get verified", desc: "Add your info, record a 60-second video intro, and complete your background check in-app.", Icon: ShieldCheck },
              { num: "02", title: "AI matches you — automatically", desc: "We score compatibility, run your profile against listings, and surface only the right matches.", Icon: Sparkles },
              { num: "03", title: "Tour, chat & schedule showings", desc: "Watch video walkthroughs, message landlords directly, and book showings in one tap.", Icon: Video },
              { num: "04", title: "Sign digitally & move in", desc: "Sign your lease digitally. Choose your lease length. Get your keys. Done.", Icon: Key },
            ].map((step, i) => (
              <motion.div
                key={step.num}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={i}
                className="relative"
              >
                <p className="text-6xl font-black text-white/10 mb-4 font-['Fraunces']">{step.num}</p>
                <h3 className="font-black text-white text-lg sm:text-xl mb-3 font-['Fraunces']">{step.title}</h3>
                <p className="text-sm text-white/50 leading-relaxed">{step.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── LEASE TYPES ─── */}
      <section className="px-5 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="max-w-7xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} className="mb-14">
            <motion.p variants={fadeUp} custom={0} className="uppercase tracking-[0.2em] text-xs font-semibold text-[#1a6b3c] mb-4">
              Flexible Lease Terms
            </motion.p>
            <motion.h2 variants={fadeUp} custom={1} className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-[#0d0d0d] font-['Fraunces']">
              Your timeline, your <em className="text-[#e8633a]">terms.</em>
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { Icon: Zap, title: "Short-Term", period: "30 – 90 Days", desc: "Perfect for travel nurses, government contractors, TDY military orders.", tags: ["Travel Nurses", "TDY Orders", "Contractors"] },
              { Icon: Clock, title: "Mid-Term", period: "3 – 12 Months", desc: "The sweet spot for PCS military families, relocating professionals.", tags: ["PCS Families", "Relocating", "Remote Workers"] },
              { Icon: Home, title: "Long-Term", period: "1 Year+", desc: "For renters who want to plant roots with the most affordable rates.", tags: ["Families", "Best Rates", "Stability"] },
            ].map((lease, i) => (
              <motion.div
                key={lease.title}
                initial="hidden" whileInView="visible" viewport={{ once: true }}
                variants={fadeUp} custom={i}
                className="bg-white rounded-3xl p-8 border border-black/[0.06] shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
              >
                <div className="w-10 h-10 rounded-xl bg-[#1a6b3c]/10 flex items-center justify-center mb-4">
                  <lease.Icon className="w-5 h-5 text-[#1a6b3c]" />
                </div>
                <h3 className="text-xl font-black text-[#0d0d0d] mb-1 font-['Fraunces']">{lease.title}</h3>
                <p className="text-sm text-[#1a6b3c] font-medium mb-3">{lease.period}</p>
                <p className="text-sm text-[#888] leading-relaxed mb-4">{lease.desc}</p>
                <div className="flex flex-wrap gap-2">
                  {lease.tags.map(tag => (
                    <Badge key={tag} variant="secondary" className="bg-[#faf8f3] text-[#0d0d0d] border border-black/[0.06] rounded-full text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── MILITARY SECTION ─── */}
      <section className="px-5 md:px-12 lg:px-24 py-20 md:py-32">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="rounded-3xl overflow-hidden relative"
          >
            <img
              src="https://images.pexels.com/photos/7983759/pexels-photo-7983759.jpeg?w=1200"
              alt="Military family"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-[#0d0d0d]/70" />
            <div className="relative z-10 p-6 sm:p-8 md:p-16 max-w-2xl">
              <motion.h2 variants={fadeUp} custom={0} className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-white mb-6 font-['Fraunces']">
                Built for <em className="text-[#c8f563]">military</em> families first.
              </motion.h2>
              <motion.p variants={fadeUp} custom={1} className="text-white/70 mb-8 leading-relaxed text-sm sm:text-base">
                PCS orders mean you need housing fast. Video tours, built-in background checks, remote applications, and BAH-eligible listings make Low Key Rents the all-in-one platform the VA, MD & DC military community has been waiting for.
              </motion.p>
              <motion.div variants={fadeUp} custom={2} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {["BAH-eligible listings", "Short, mid & long-term leases", "VA · Arlington · Alexandria · Fairfax", "Quantico · Fort Belvoir · Stafford", "DC · Bethesda · Silver Spring · Rockville"].map(item => (
                  <div key={item} className="flex items-center gap-2 text-white text-sm">
                    <Check className="w-4 h-4 text-[#c8f563] flex-shrink-0" />
                    <span>{item}</span>
                  </div>
                ))}
              </motion.div>
              <Button
                data-testid="military-cta"
                onClick={!user ? handleLogin : () => window.location.href = "/browse?bah_eligible=true"}
                className="rounded-full bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] px-8 h-12 text-base font-semibold"
              >
                Military Families <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ─── CTA FOOTER ─── */}
      <section className="px-5 md:px-12 lg:px-24 py-20 md:py-32 bg-[#0d0d0d]">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-white mb-8 font-['Fraunces']">
            Your next home is <em className="text-[#c8f563]">waiting.</em>
          </h2>
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            <Button
              data-testid="footer-cta-renter"
              onClick={!user ? handleLogin : () => window.location.href = "/browse"}
              className="rounded-full bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] px-6 sm:px-8 h-12 text-sm sm:text-base font-semibold"
            >
              <Bed className="w-5 h-5 mr-2" /> I Need a Place
            </Button>
            <Button
              data-testid="footer-cta-landlord"
              onClick={!user ? handleLogin : () => window.location.href = "/landlord-dashboard"}
              variant="outline"
              className="rounded-full border-white text-white hover:bg-white hover:text-[#0d0d0d] px-6 sm:px-8 h-12 text-sm sm:text-base font-semibold"
            >
              <Home className="w-5 h-5 mr-2" /> I Have a Place
            </Button>
            <Button
              data-testid="footer-cta-military"
              onClick={!user ? handleLogin : () => window.location.href = "/browse?bah_eligible=true"}
              variant="outline"
              className="rounded-full border-[#c8f563] text-[#c8f563] hover:bg-[#c8f563] hover:text-[#0d0d0d] px-6 sm:px-8 h-12 text-sm sm:text-base font-semibold"
            >
              <ShieldCheck className="w-5 h-5 mr-2" /> Military Family
            </Button>
          </div>
          <p className="text-white/40 text-sm mt-8">No spam. No credit card. Just your spot in line.</p>
        </div>
      </section>

      {/* ─── FOOTER ─── */}
      <footer className="px-5 md:px-12 lg:px-24 py-12 border-t border-black/[0.06]">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 mb-10">
            <div className="sm:col-span-1">
              <Logo size="small" />
              <p className="text-xs text-[#888] mt-3 leading-relaxed">Verified rooms, apartments & houses across the DMV.</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#0d0d0d] uppercase tracking-wider mb-3">Platform</p>
              <div className="space-y-2">
                <Link to="/browse" className="block text-sm text-[#888] hover:text-[#1a6b3c] transition-colors">Browse Listings</Link>
                <Link to="/auth" className="block text-sm text-[#888] hover:text-[#1a6b3c] transition-colors">Sign In / Register</Link>
                <Link to="/contact" className="block text-sm text-[#888] hover:text-[#1a6b3c] transition-colors">Contact Us</Link>
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#0d0d0d] uppercase tracking-wider mb-3">Virginia</p>
              <div className="space-y-2">
                {["Norfolk", "Virginia Beach", "Arlington", "Alexandria", "Falls Church", "Fairfax", "Reston", "Manassas"].map(city => (
                  <Link key={city} to={`/browse?city=${city}`} className="block text-sm text-[#888] hover:text-[#1a6b3c] transition-colors">{city}</Link>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#0d0d0d] uppercase tracking-wider mb-3">DC & Maryland</p>
              <div className="space-y-2">
                {["Washington DC", "Bethesda", "Silver Spring", "Rockville", "College Park", "Bowie", "Largo", "Frederick"].map(city => (
                  <Link key={city} to={`/browse?city=${city}`} className="block text-sm text-[#888] hover:text-[#1a6b3c] transition-colors">{city}</Link>
                ))}
              </div>
            </div>
            <div>
              <p className="text-xs font-semibold text-[#0d0d0d] uppercase tracking-wider mb-3">Contact</p>
              <div className="space-y-2">
                <a href="mailto:hello@lowkey.rent" className="block text-sm text-[#888] hover:text-[#1a6b3c] transition-colors" data-testid="footer-email-hello">hello@lowkey.rent</a>
                <a href="mailto:support@lowkey.rent" className="block text-sm text-[#888] hover:text-[#1a6b3c] transition-colors" data-testid="footer-email-support">support@lowkey.rent</a>
              </div>
            </div>
          </div>
          <div className="border-t border-black/[0.06] pt-6 flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-xs text-[#888]">
              &copy; 2026 Low Key Rents. All rights reserved.
              <Link
                to="/admin"
                aria-label="Admin"
                title="Admin"
                data-testid="footer-admin-link"
                className="ml-1 text-[#faf8f3] hover:text-[#1a6b3c] transition-colors select-none"
              >
                ·
              </Link>
            </p>
            <div className="flex items-center gap-4">
              <Link to="/terms" className="text-xs text-[#888] hover:text-[#1a6b3c]">Terms</Link>
              <Link to="/privacy" className="text-xs text-[#888] hover:text-[#1a6b3c]">Privacy</Link>
              <Link to="/contact" className="text-xs text-[#888] hover:text-[#1a6b3c]">Contact</Link>
              <a href="mailto:support@lowkey.rent" className="text-xs text-[#888] hover:text-[#1a6b3c]">Support</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
